import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Printer } from 'lucide-react'
import { service } from '../lib/api/service'
import { entryToATA } from '../lib/api/transform'
import type { Project, ATAView } from '../lib/api/types'
import { formatSEK } from '../utils/calculations'
import { statusInfo } from '../utils/statusUtils'

export default function JournalPrint() {
  const navigate = useNavigate()

  const [project, setProject]   = useState<Project | null>(null)
  const [atas, setAtas]         = useState<ATAView[]>([])
  const [loading, setLoading]   = useState(true)

  useEffect(() => {
    Promise.all([service.getProject(), service.getAllEntries(), service.getSurcharges()])
      .then(([proj, entries, sc]) => {
        setProject(proj)
        setAtas(entries.map(e => entryToATA(e, sc)))
      })
      .finally(() => setLoading(false))
  }, [])

  const doPrint = () => {
    document.body.dataset.printing = '1'
    window.print()
    delete document.body.dataset.printing
  }

  if (loading) return <div className="loading"><div className="spinner" /></div>

  const proj: Partial<Project> = project ?? {}
  const today  = new Date().toISOString().slice(0, 10)
  const total  = atas.reduce((s, a) => s + (a.belopp ?? 0), 0)

  return (
    <div className="print-overlay">
      <div className="print-toolbar no-print">
        <button className="icon-btn" onClick={() => navigate('/journal')}>
          <ArrowLeft size={14} /> Tillbaka
        </button>
        <span style={{ fontSize: 13, color: 'var(--ink-dim)' }}>
          Underrättelsejournal · {atas.length} poster
        </span>
        <button className="btn-primary" style={{ marginLeft: 'auto' }} onClick={doPrint}>
          <Printer size={14} /> Skriv ut
        </button>
      </div>

      <div className="paper">
        {/* Letterhead */}
        <div className="paper-letterhead">
          <div>
            <div className="paper-sender-name">TIBRAM BYGG AB</div>
            <div className="paper-sender-addr">
              {proj.adress && <>{proj.adress}<br /></>}
              {proj.postadress ?? ''}
            </div>
          </div>
          <div>
            <div className="paper-doc-type">Underrättelsejournal</div>
            <div className="paper-doc-ref">
              {proj.projektnr ? `Projekt ${proj.projektnr}` : 'ÄTA Journal'}
            </div>
          </div>
        </div>

        <div style={{ fontSize: '9pt', color: '#666', margin: '8pt 0 14pt', display: 'flex', gap: '16pt' }}>
          <span>Utskriven: {today}</span>
          {proj.projektNamn && <span>{proj.projektNamn}{proj.ort ? ', ' + proj.ort : ''}</span>}
          <span>{atas.length} poster</span>
        </div>

        <table>
          <thead>
            <tr>
              <th style={{ width: '55pt' }}>Nr</th>
              <th>Rubrik</th>
              <th style={{ width: '70pt' }}>Orsak</th>
              <th style={{ width: '75pt' }}>Status</th>
              <th style={{ width: '60pt' }}>Anmäld</th>
              <th className="r" style={{ width: '75pt' }}>Belopp</th>
            </tr>
          </thead>
          <tbody>
            {atas.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: '#999', padding: '12pt 0' }}>
                  Inga ÄTA registrerade
                </td>
              </tr>
            )}
            {atas.map(a => (
              <tr key={a.sortOrder}>
                <td className="code">ÄTA-{String(a.sortOrder).padStart(3, '0')}</td>
                <td>{a.rubrik || '–'}</td>
                <td style={{ fontSize: '9pt', color: '#555' }}>{a.orsak || '–'}</td>
                <td style={{ fontSize: '9pt' }}>{statusInfo(a.status).kort}</td>
                <td className="code" style={{ fontSize: '9pt', color: '#555' }}>{a.anmald ?? '–'}</td>
                <td className="r">{a.belopp != null ? formatSEK(a.belopp) : '–'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="sum-line">
              <td colSpan={5} style={{ textAlign: 'right' }}>Summa prissatt</td>
              <td className="r">{formatSEK(total)}</td>
            </tr>
          </tfoot>
        </table>

        <div className="paper-footer">
          {proj.projektnr ?? 'ÄTA Journal'} &nbsp;—&nbsp; Utskriven {today}
        </div>
      </div>
    </div>
  )
}
